import { useMediaCapture, useDeviceList } from '@features/media-capture';
import {
  SelectField,
  CameraIcon,
  MicIcon,
  MonitorIcon,
  AudioWaveIcon,
} from '@shared/ui';
import { SUPPORTED_RESOLUTIONS, SUPPORTED_FRAME_RATES } from '@shared/types';
import { formatResolutionLabel } from '@entities/device';

/**
 * Camera / microphone / resolution / FPS controls.
 *
 * Filters that apply to the outgoing stream live in `VideoFilterPanel` —
 * that UX is wired to the streaming feature, not the capture device list.
 */
export function DeviceToolbar() {
  const { videoInputs, audioInputs } = useDeviceList();
  const { settings, updateSettings } = useMediaCapture();

  const resolutionOptions = SUPPORTED_RESOLUTIONS.map((res) => ({
    value: formatResolutionLabel(res.width, res.height),
    label: formatResolutionLabel(res.width, res.height),
  }));

  const fpsOptions = SUPPORTED_FRAME_RATES.map((fps) => ({
    value: String(fps),
    label: String(fps),
  }));

  return (
    <div className="flex flex-wrap items-center gap-6 rounded-xl border border-surface-border bg-surface-1 px-5 py-4">
      <SelectField
        icon={<CameraIcon className="h-5 w-5" />}
        label="Camera"
        value={settings.videoDeviceId ?? ''}
        onChange={(event) => void updateSettings({ videoDeviceId: event.target.value || null })}
        options={[
          { value: '', label: videoInputs[0]?.label ?? 'No camera found' },
          ...videoInputs.map((device) => ({ value: device.id, label: device.label })),
        ]}
      />

      <SelectField
        icon={<MicIcon className="h-5 w-5" />}
        label="Microphone"
        value={settings.audioDeviceId ?? ''}
        onChange={(event) => void updateSettings({ audioDeviceId: event.target.value || null })}
        options={[
          { value: '', label: audioInputs[0]?.label ?? 'No microphone found' },
          ...audioInputs.map((device) => ({ value: device.id, label: device.label })),
        ]}
      />

      <SelectField
        icon={<MonitorIcon className="h-5 w-5" />}
        label="Resolution"
        value={formatResolutionLabel(settings.resolution.width, settings.resolution.height)}
        onChange={(event) => {
          const match = SUPPORTED_RESOLUTIONS.find(
            (res) => formatResolutionLabel(res.width, res.height) === event.target.value,
          );
          if (match) void updateSettings({ resolution: match });
        }}
        options={resolutionOptions}
      />

      <SelectField
        icon={<AudioWaveIcon className="h-5 w-5" />}
        label="FPS"
        value={String(settings.frameRate)}
        onChange={(event) =>
          void updateSettings({
            frameRate: Number(event.target.value) as typeof settings.frameRate,
          })
        }
        options={fpsOptions}
      />
    </div>
  );
}
